import React, { useEffect, useState } from "react";
import {
    Form,
    Input,
    Select,
    Upload,
    Button,
    message
} from "antd";
import {
    UploadOutlined,
    UserOutlined,
    FilePdfOutlined,
    DeleteOutlined,
    FileImageOutlined,
    FileWordOutlined,
} from "@ant-design/icons";
import { getAllSources, applyJob } from "../../utils/API";
import gujaratCities from "./gujaratcities.json";

interface JobApply {
    _id: string;
    full_name: string;
    date: moment.Moment;
    email: string;
    phone_number: string;
    city: string;
    technology: string;
    total_experience: number;
    relevant_experience: number;
    current_ctc: number;
    expected_ctc: number;
    source: string;
    resume?: string;
    jobDesc: string;
    status: string;
}

const ApplyJob = () => {
    const [form] = Form.useForm();
    const imageUrl = process.env.REACT_APP_Image_URL || "";
    const [page, setPage] = useState(1);
    const [searchText, setSearchText] = useState("");
    const [sources, setSources] = useState<Array<string>>([]);
    const [source, setSource] = useState<string | undefined>();
    const [resumes, setResumes] = useState<Array<string | File>>([]);
    const [fileList, setFileList] = useState<any[]>([]);
    const [position, setPosition] = useState<string | undefined>();
    const [loading, setLoading] = useState(false);

    // Static options by position
    // Static options by position
    const technologyOptions: Record<string, string[]> = {
        "Frontend Developer": [
            // 🌐 Core
            "HTML5",
            "CSS3",
            "JavaScript (ES6+)",
            "TypeScript",

            // ⚛️ Frameworks & Libraries
            "React",
            "Next.js",
            "Angular",
            "Vue.js",
            "Nuxt.js",
            "Svelte",
            "SvelteKit",

            // 🎨 Styling & UI
            "Tailwind CSS",
            "Bootstrap",
            "Material UI",
            "Chakra UI",
            "Ant Design",

            // 📦 State Management
            "Redux",
            "Zustand",
            "Recoil",
            "Pinia",

            // ⚡ Build Tools
            "Vite",
            "Webpack",

            // 🔧 Testing
            "Jest",
            "React Testing Library",
            "Cypress",
            "Playwright",

            // 📱 Cross-Platform
            "React Native",
            "Flutter (Web Support)",

            // 📐 Static Site / Modern Meta Frameworks
            "Astro",
            "Gatsby",

            // 🛠 Other
            "GraphQL (Apollo)",
            "Storybook",
            "ESLint",
            "Prettier"
        ],
        "Backend Developer": [
            // ⚙️ Languages
            "JavaScript (Node.js)",
            "TypeScript",
            "Python",
            "Java",
            "C#",
            "PHP",
            "Go",
            "Ruby",
            "Rust",
            "Kotlin",
            "Scala",

            // 🖥 Frameworks & Runtimes
            "Node.js",
            "Express.js",
            "NestJS",
            "Django",
            "Flask",
            "FastAPI",
            "Spring Boot",
            "Micronaut",
            "Quarkus",
            "Laravel",
            "Symfony",
            "CodeIgniter",
            "Ruby on Rails",
            "Phoenix (Elixir)",
            "Fiber (Go)",
            "Gin (Go)",
            "Actix (Rust)",
            "Rocket (Rust)",

            // 🗄 Databases
            "MySQL",
            "PostgreSQL",
            "SQLite",
            "MongoDB",
            "Redis",
            "MariaDB",
            "Oracle DB",
            "MS SQL Server",
            "Cassandra",
            "CouchDB",
            "Firebase Realtime DB",
            "DynamoDB",

            // 📡 APIs & Communication
            "REST API",
            "GraphQL",
            "gRPC",
            "WebSockets",

            // ☁️ Cloud & Serverless
            "AWS Lambda",
            "Google Cloud Functions",
            "Azure Functions",
            "Serverless Framework",

            // 🐳 DevOps Tools
            "Docker",
            "Kubernetes",

            // 🧪 Testing
            "JUnit",
            "PyTest",
            "Mocha",
            "Jest",
            "Supertest",
            "Postman/Newman"
        ],
        "Fullstack Developer": [
            // 🌐 JavaScript/TypeScript Stacks
            "MERN (MongoDB, Express.js, React, Node.js)",
            "MEAN (MongoDB, Express.js, Angular, Node.js)",
            "MEVN (MongoDB, Express.js, Vue.js, Node.js)",
            "Next.js + Node.js",
            "NestJS + React",
            "RedwoodJS (Fullstack React/GraphQL framework)",
            "Blitz.js (Next.js based fullstack framework)",

            // 🐘 Python Based
            "Django + React",
            "Django + Angular",
            "Flask + React",
            "FastAPI + Vue",

            // ☕ Java Based
            "Spring Boot + React",
            "Spring Boot + Angular",

            // 🐘 PHP Based
            "LAMP (Linux, Apache, MySQL, PHP)",
            "Laravel + Vue.js",
            "Laravel + React",
            "Symfony + React",

            // 🚀 Other Popular Stacks
            "Ruby on Rails + React",
            "Ruby on Rails + Hotwire",
            "Elixir Phoenix + LiveView",
            "Go + React",
            "Go + Vue",
            "ASP.NET Core + React",
            "ASP.NET Core + Angular",

            // ☁️ Modern Fullstack/Meta Frameworks
            "Remix (React Fullstack Framework)",
            "Nuxt.js (Vue Fullstack Framework)",
            "SvelteKit (Svelte Fullstack Framework)",
            "Astro (with React/Vue/Svelte integrations)"
        ],
        "App Developer": [
              // Native Platforms
            "Android (Kotlin, Java)",
            "iOS (Swift, Objective-C)",
            // Mobile App Frameworks
            "React Native",
            "Flutter",
            "Ionic",
            "Xamarin",
        ]

        // "UI/UX Designer": ["Figma", "Adobe XD", "Sketch", "InVision"],
        // "Business Development Executive": ["Lead Generation", "Client Communication", "Salesforce"],
    };

    const onFinish = async (values: any) => {
        setLoading(true);
        try {
            const formData = new FormData();

            // If no relevant technology chosen, fall back to position
            if (!values.technology && values.position) {
                formData.append("technology", values.position);
            } else if (values.technology) {
                formData.append("technology", values.technology);
            }

            // Append other fields except technology (already handled)
            Object.entries(values).forEach(([key, value]) => {
                if (key === "technology") return; // skip, already appended
                if (value !== undefined && value !== null) {
                    formData.append(key, value as string);
                }
            });

            // Append resume if exists
            if (resumes.length > 0 && resumes[0] instanceof File) {
                formData.append("resume", resumes[0]);
            }

            await applyJob(formData);
            message.success("Job application submitted successfully!");

            // Reset after success
            form.resetFields();
            setResumes([]);
            setFileList([]);
            setPosition(undefined);
            setSource(undefined);
        } catch (err: any) {
            console.error("Error submitting job application:", err);

            // 🔹 Handle backend validation errors
            if (err.response?.data?.errors) {
                // Example structure: { errors: { email: "Email already exists", phone_number: "Invalid phone number" } }
                const errorFields = Object.entries(err.response.data.errors).map(([field, msg]) => ({
                    name: field,
                    errors: [msg as string],
                }));

                form.setFields(errorFields);
            } else {
                // Fallback generic error
                message.error(err.response?.data?.message || "Failed to submit job application");
            }
        } finally {
            setLoading(false);
        }
    };

    const fetchSource = async () => {
        try {
            const getSource = await getAllSources(searchText, page);
            if (getSource) {
                let filteredSource = getSource.sources.filter((item: any) =>
                    item.category.includes("JobApplication")
                );
                setSources(filteredSource);
            }
        } catch (error) {
            console.error("Error fetching source:", error);
        }
    };

    const handleDeleteDocument = (doc: string | File, index: number) => {
        const newResumes = [...resumes];
        newResumes.splice(index, 1);
        setResumes(newResumes);

        const newFileList = [...fileList];
        newFileList.splice(index, 1);
        setFileList(newFileList);
    };

    useEffect(() => {
        fetchSource();
    }, []);

    const beforeUpload = (file: File) => {
        const isAllowed =
            file.type === "application/pdf" || // PDF
            file.type === "application/msword" || // DOC
            file.type ===
            "application/vnd.openxmlformats-officedocument.wordprocessingml.document" || // DOCX
            file.type === "image/jpeg" || // JPG/JPEG
            file.type === "image/png"; // PNG

        if (!isAllowed) {
            message.error("Only PDF, DOC, DOCX, JPG, JPEG, PNG files are allowed.");
            return Upload.LIST_IGNORE;
        }

        const isLt10M = file.size / 1024 / 1024 < 10;
        if (!isLt10M) {
            message.error("Resume must be smaller than 10MB!");
            return Upload.LIST_IGNORE; // prevent upload
        }

        return false; // keep file in list, prevent auto-upload
    };

    const getFileIcon = (fileName: string) => {
        const ext = fileName.split(".").pop()?.toLowerCase();

        if (ext === "pdf") return <FilePdfOutlined className="text-2xl text-red-500 mb-1" />;
        if (ext === "doc" || ext === "docx") return <FileWordOutlined className="text-2xl text-blue-500 mb-1" />;
        if (ext === "jpg" || ext === "jpeg" || ext === "png")
            return <FileImageOutlined className="text-2xl text-green-500 mb-1" />;

        // default fallback
        return <FilePdfOutlined className="text-2xl text-gray-500 mb-1" />;
    };



    return (
        <div className="flex items-start justify-center min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black">
            <div className="w-full max-w-5xl bg-white rounded-2xl shadow-xl mt-10 mb-10 mx-4 sm:mx-6 lg:mx-10">
                {/* Title */}
                <div>
                    <h1 className="text-2xl font-bold flex items-center gap-2 p-3   ">
                        <UserOutlined /> Candidate Details Form
                    </h1>
                </div>
                <div className="footer-modals" />

                {/* Form */}
                <Form layout="vertical" onFinish={onFinish} form={form}>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4  p-6">
                        {/* Full Name */}
                        <Form.Item
                            name="full_name"
                            label={
                                <span className="Inputlabel">
                                    Full Name <span className="text-red-500">*</span>
                                </span>
                            }
                            required={false}
                            rules={[{ required: true, message: "Please enter your full name" }]}
                        >
                            <Input placeholder="Full name" className="Allinput" />
                        </Form.Item>

                        {/* Email */}
                        <Form.Item
                            label={
                                <span className="Inputlabel">
                                    Email <span className="text-red-500">*</span>
                                </span>
                            }
                            required={false}

                            name="email"
                            rules={[
                                { required: true, message: "Please enter your email" },
                                { type: "email", message: "Please enter a valid email" },
                            ]}
                        >
                            <Input placeholder="Email" className="Allinput" />
                        </Form.Item>

                        {/* Contact */}
                        <Form.Item
                            label={
                                <span className="Inputlabel">
                                    Contact No. <span className="text-red-500">*</span>
                                </span>
                            }
                            required={false}

                            name="phone_number"
                            rules={[
                                { required: true, message: "Please enter your phone number" },
                                {
                                    pattern: /^[0-9+\- ]+$/,
                                    message: "Please enter a valid phone number",
                                },
                            ]}
                        >
                            <Input placeholder="Contact" className="Allinput" />
                        </Form.Item>

                        {/* City */}
                        <Form.Item
                            label={
                                <span className="Inputlabel">
                                    City <span className="text-red-500">*</span>
                                </span>
                            }
                            required={false}
                            name="city"
                            rules={[{ required: true, message: "Please select your city" }]}
                        >
                            <Select
                                placeholder="Select City"
                                className="CustomSelect"
                                showSearch
                                optionFilterProp="children"
                                filterOption={(input, option) =>
                                    (String(option?.children) ?? "").toLowerCase().includes(input.toLowerCase())
                                }
                            >
                                {gujaratCities.map((city) => (
                                    <Select.Option key={city} value={city}>
                                        {city}
                                    </Select.Option>
                                ))}
                            </Select>
                        </Form.Item>


                        {/* Position */}
                        <Form.Item
                            name="position"
                            label={
                                <span className="Inputlabel">
                                    Position <span className="text-red-500">*</span>
                                </span>
                            }
                            required={false}

                            rules={[{ required: true, message: "Please select the position" }]}
                        >
                            <Select
                                placeholder="Select Position"
                                className="CustomSelect"
                                value={position}
                                onChange={(value) => {
                                    setPosition(value);
                                    form.setFieldsValue({ technology: undefined });
                                }}
                            >
                                <Select.Option value="Frontend Developer">Frontend Developer</Select.Option>
                                <Select.Option value="Backend Developer">Backend Developer</Select.Option>
                                <Select.Option value="Fullstack Developer">Fullstack Developer</Select.Option>
                                <Select.Option value="App Developer">App Developer</Select.Option>

                                <Select.Option value="UI/UX Designer">
                                    UI/UX Designer
                                </Select.Option>
                                <Select.Option value="Business Development Executive">
                                    Business Development Executive
                                </Select.Option>
                            </Select>
                        </Form.Item>

                        {/* Technology - Conditional */}
                        {position && technologyOptions[position] && (
                            <Form.Item
                                label={
                                    <span className="Inputlabel">
                                        Relevant Technology
                                    </span>
                                }
                                name="technology"
                            >
                                <Select
                                    placeholder="Select Technology"
                                    className="CustomSelect"
                                    showSearch
                                    optionFilterProp="children"
                                    filterOption={(input, option) =>
                                        String(option?.children || "")
                                            .toLowerCase()
                                            .includes(input.toLowerCase())
                                    }
                                >
                                    {technologyOptions[position]?.map((tech) => (
                                        <Select.Option key={tech} value={tech}>
                                            {tech}
                                        </Select.Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        )}


                        {/* Source */}
                        <Form.Item
                            label={
                                <span className="Inputlabel">
                                    Reference From <span className="text-red-500">*</span>
                                </span>
                            }
                            name="source"
                            required={false}

                            rules={[{ required: true, message: "Please select the reference" }]}
                        >
                            <Select
                                placeholder="Select Reference"
                                className="CustomSelect"
                                value={source}
                                onChange={(value) => setSource(value)}
                            >
                                {sources.map((source: any) => (
                                    <Select.Option key={source._id} value={source._id}>
                                        {source.name}
                                    </Select.Option>
                                ))}
                            </Select>
                        </Form.Item>

                        {/* Experience */}
                        <Form.Item
                            label={
                                <span className="Inputlabel">
                                    Total Experience <span className="text-red-500">*</span>
                                </span>
                            }
                            required={false}
                            name="total_experience"
                            rules={[{ required: true, message: "Please enter total experience" }]}
                            extra={<span className="text-gray-400 text-sm">Enter <b>0</b> if you are a fresher</span>}
                        >
                            <Input placeholder="Total experience" className="Allinput" />
                        </Form.Item>

                        <Form.Item
                            label={
                                <span className="Inputlabel">
                                    Relevant Experience <span className="text-red-500">*</span>
                                </span>
                            }
                            required={false}

                            name="relevant_experience"
                            rules={[
                                { required: true, message: "Please enter relevant experience" },
                            ]}
                            extra={<span className="text-gray-400 text-sm">Enter <b>0</b> if you are a fresher</span>}
                        >
                            <Input placeholder="Relevant experience" className="Allinput" />
                        </Form.Item>

                        <Form.Item
                            label={
                                <span className="Inputlabel">
                                    Current CTC <span className="text-red-500">*</span>
                                </span>
                            }
                            required={false}

                            name="current_ctc"
                            rules={[{ required: true, message: "Please enter current CTC" }]}
                            extra={<span className="text-gray-400 text-sm">Enter <b>0</b> if you are a fresher</span>}
                        >
                            <Input placeholder="Current CTC" className="Allinput" />
                        </Form.Item>

                        <Form.Item

                            label={
                                <span className="Inputlabel">
                                    Expected CTC <span className="text-red-500">*</span>
                                </span>
                            }
                            required={false}

                            name="expected_ctc"
                            rules={[{ required: true, message: "Please enter expected CTC" }]}
                            extra={<span className="text-gray-400 text-sm">Enter <b>0</b> if you are a fresher</span>}
                        >
                            <Input placeholder="Expected CTC" className="Allinput" />
                        </Form.Item>

                        {/* Description - full width */}
                        <Form.Item
                            className="md:col-span-2"
                            label={
                                <span className="Inputlabel">
                                    Description <span className="text-red-500">*</span>
                                </span>
                            }
                            required={false}

                            name="jobDesc"
                            rules={[{ required: true, message: "Enter job description" }]}
                        >
                            <Input.TextArea
                                rows={1}
                                autoSize={{ minRows: 1, maxRows: 5 }}
                                placeholder="Enter job description"
                                className="Allinputtextarea"
                            />
                        </Form.Item>

                        {/* Resume Upload - full width */}
                        <Form.Item
                            className="md:col-span-2"
                            label={
                                <span className="Inputlabel">
                                    Resume <span className="text-red-500">*</span>
                                </span>
                            }
                            rules={[{ required: true, message: "Upload Resume" }]}
                            extra={
                                <span className="text-gray-500 text-sm">
                                    Maximum file size: 10 MB.<br /> Supported format: PDF / DOCX / DOC / JPG /
                                    JPEG / PNG.
                                </span>
                            }
                            required={false}
                            name="resume"
                        >
                            <div className="flex items-center gap-4 flex-wrap">
                                <Upload
                                    listType="picture-card"
                                    maxCount={1}
                                    showUploadList={false}
                                    disabled={resumes.length > 0}
                                    beforeUpload={beforeUpload}   // ✅ attach function here
                                    onChange={(info) => {
                                        if (info.fileList.length > 0 && resumes.length === 0) {
                                            const newFile = info.fileList[0].originFileObj;
                                            if (newFile) {
                                                setResumes([newFile]);
                                                setFileList([
                                                    {
                                                        uid: `new-${Date.now()}`,
                                                        name: newFile.name,
                                                        status: "done",
                                                        url: URL.createObjectURL(newFile),
                                                    },
                                                ]);
                                            }
                                        }
                                    }}
                                >
                                    <Button icon={<UploadOutlined />} disabled={resumes.length > 0}>
                                        Upload
                                    </Button>
                                </Upload>

                                {/* Resume Preview */}
                                {resumes.map((doc, index) => {
                                    if (!doc) return null;
                                    const fileName =
                                        typeof doc === "string"
                                            ? doc.split("/").pop() || ""
                                            : (doc as File).name;
                                    const fileUrl =
                                        typeof doc === "string"
                                            ? `${imageUrl}${doc}`
                                            : URL.createObjectURL(doc as File);

                                    return (
                                        <div
                                            key={index}
                                            className="w-36 h-28 border-2 border-gray-200 rounded-lg bg-white relative flex flex-col items-center justify-center p-2"
                                        >
                                            {getFileIcon(fileName)}
                                            <span className="text-xs font-medium text-gray-700 truncate w-full text-center">
                                                {fileName}
                                            </span>
                                            <a
                                                href={fileUrl}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="absolute inset-0"
                                                onClick={(e) => e.stopPropagation()}
                                            ></a>
                                            <button
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    handleDeleteDocument(doc, index);
                                                }}
                                                className="absolute top-1 right-1 text-red-500 hover:text-red-700 bg-white rounded-full p-1"
                                                style={{ width: "20px", height: "20px" }}
                                            >
                                                <DeleteOutlined style={{ fontSize: "12px" }} />
                                            </button>
                                        </div>
                                    );
                                })}
                            </div>
                        </Form.Item>

                    </div>
                    <div className="footer-modals" />

                    {/* Submit */}
                    <div className="flex justify-end  px-5">
                        <button
                            type="submit"
                            className="allsubmitebutton  w-48  "
                        >
                            Submit Application
                        </button>
                    </div>
                </Form>
            </div>
        </div>
    );
};

export default ApplyJob;
